import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { moneyFormatter, getRequestParams } from '../../helpers/utils';
import PayoutService from '../../services/payout.service';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

const STATUS_CHIP = {
  paid: { label: 'Paid', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  in_transit: { label: 'In transit', color: 'info' },
  pending_reconciliation: { label: 'Pending reconciliation', color: 'info' },
  canceled: { label: 'Canceled', color: 'default' },
  failed: { label: 'Failed', color: 'error' }
};

const pageSizes = [10, 20, 30];

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

const formatAmount = (amount, currency) => {
  if (amount == null) return '-';
  return `${(currency || '').toUpperCase()} ${moneyFormatter(amount, currency)}`;
};

const PayoutRow = ({ payout, onOpenReceipt, receiptLoadingUuid }) => {
  const [open, setOpen] = useState(false);
  const transactions = payout.balanceTransaction || [];
  const aggregateNet = transactions.reduce((sum, t) => sum + (t.net ?? 0), 0);
  const statusMeta = STATUS_CHIP[payout.status] || { label: payout.status, color: 'default' };

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={() => setOpen((v) => !v)} disabled={transactions.length === 0}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{formatDate(payout.arrivalDate)}</TableCell>
        <TableCell>{formatDate(payout.created)}</TableCell>
        <TableCell>
          <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
          {payout.method && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {payout.method}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">{formatAmount(payout.amount, payout.currency)}</TableCell>
        <TableCell align="right">{transactions.length}</TableCell>
        <TableCell align="right">{formatAmount(aggregateNet, payout.currency)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Transactions
              </Typography>
              {transactions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No transactions linked to this payout yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Fee</TableCell>
                      <TableCell align="right">Net</TableCell>
                      <TableCell align="center" sx={{ width: 56 }}>Receipt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((t) => (
                      <TableRow key={t.uuid}>
                        <TableCell>{formatDate(t.created)}</TableCell>
                        <TableCell>
                          {t.customer ? (
                            <>
                              <Typography variant="body2">{t.customer.name || t.customerName || '-'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t.customer.email || t.customerEmail}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2">{t.customerName || t.customerEmail || '-'}</Typography>
                          )}
                        </TableCell>
                        <TableCell>{t.product?.name || '-'}</TableCell>
                        <TableCell align="right">
                          {t.fee != null ? formatAmount(t.fee, t.currency) : '-'}
                        </TableCell>
                        <TableCell align="right">{formatAmount(t.net, t.currency)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Open invoice or receipt">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => onOpenReceipt(t)}
                                disabled={receiptLoadingUuid === t.uuid}
                              >
                                {receiptLoadingUuid === t.uuid ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <ReceiptLongIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

PayoutRow.propTypes = {
  onOpenReceipt: PropTypes.func.isRequired,
  receiptLoadingUuid: PropTypes.string,
  payout: PropTypes.shape({
    uuid: PropTypes.string,
    amount: PropTypes.number,
    currency: PropTypes.string,
    created: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    arrivalDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    method: PropTypes.string,
    status: PropTypes.string,
    balanceTransaction: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string,
        net: PropTypes.number,
        fee: PropTypes.number,
        currency: PropTypes.string,
        created: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
        customerName: PropTypes.string,
        customerEmail: PropTypes.string,
        customer: PropTypes.shape({
          name: PropTypes.string,
          email: PropTypes.string,
          country: PropTypes.string
        }),
        product: PropTypes.shape({
          name: PropTypes.string
        })
      })
    )
  }).isRequired
};

const ScheduleLockPanel = ({ accountId }) => {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    PayoutService.getScheduleState(accountId)
      .then((response) => setState(response.data))
      .catch((e) => {
        console.log(e);
        setError(e?.response?.data?.message || 'Could not load the payout schedule.');
      });
  }, [accountId]);

  const toggleLock = async () => {
    const locking = !state.payoutScheduleLocked;
    const message = locking
      ? 'Lock payouts for this account? Automatic payouts stop and the merchant cannot change the schedule.'
      : 'Unlock payouts? The schedule stays paused until someone changes it.';
    if (!window.confirm(message)) return;

    setSaving(true);
    setError(null);
    try {
      const { data } = await PayoutService.setScheduleLock(accountId, locking);
      setState({ payoutSchedule: data.payoutSchedule, payoutScheduleLocked: data.payoutScheduleLocked });
    } catch (e) {
      console.log(e);
      setError(e?.response?.data?.message || 'Could not update the payout lock.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs>
          <Typography variant="subtitle2">Payout schedule</Typography>
          {state ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip label={state.payoutSchedule} size="small" />
              {state.payoutScheduleLocked && <Chip label="Locked" color="error" size="small" />}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          )}
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(state?.payoutScheduleLocked)}
                onChange={toggleLock}
                disabled={!state || saving}
                color="error"
              />
            }
            label="Lock payouts"
          />
        </Grid>
      </Grid>
      {state?.payoutScheduleLocked && (
        <Typography variant="caption" color="text.secondary">
          Payouts are pinned to manual. Neither the merchant nor the subscription webhook can re-enable them.
        </Typography>
      )}
      {error && (
        <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

ScheduleLockPanel.propTypes = {
  accountId: PropTypes.string.isRequired
};

const Payouts = () => {
  const params = useParams();
  const accountId = params.accountId;

  const [payouts, setPayouts] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [count, setCount] = useState(0);
  const [receiptLoadingUuid, setReceiptLoadingUuid] = useState(null);
  const [receiptError, setReceiptError] = useState(null);

  const openReceipt = async (balanceTransaction) => {
    setReceiptLoadingUuid(balanceTransaction.uuid);
    setReceiptError(null);
    try {
      const { data } = await PayoutService.getBalanceTransactionReceipt(accountId, balanceTransaction.uuid);
      const url = data.invoicePdfUrl || data.hostedInvoiceUrl || data.receiptUrl;
      if (!url) {
        setReceiptError('No invoice or receipt is available for this transaction.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.log(e);
      setReceiptError(e?.response?.data?.message || 'Could not load invoice or receipt.');
    } finally {
      setReceiptLoadingUuid(null);
    }
  };

  useEffect(() => {
    setPayouts(null);
    const reqParams = getRequestParams('', page, pageSize);
    PayoutService.getAll(accountId, reqParams)
      .then((response) => {
        setPayouts(response.data.payouts || []);
        setCount(response.data.totalPages || 0);
      })
      .catch((e) => {
        console.log(e);
        setPayouts([]);
      });
  }, [accountId, page, pageSize]);

  const handlePageChange = (event, value) => setPage(value);
  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1);
  };

  if (payouts === null) {
    return (
      <div className="container">
        <ScheduleLockPanel accountId={accountId} />
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (payouts.length === 0) {
    return (
      <div className="container">
        <ScheduleLockPanel accountId={accountId} />
        <Box sx={{ mt: 2, mb: 2 }}>No payouts found.</Box>
      </div>
    );
  }

  return (
    <div className="container">
      <ScheduleLockPanel accountId={accountId} />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Estimated arrival</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right"># Transactions</TableCell>
              <TableCell align="right">Transactions total (net)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts.map((p) => (
              <PayoutRow
                key={p.uuid}
                payout={p}
                onOpenReceipt={openReceipt}
                receiptLoadingUuid={receiptLoadingUuid}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid sx={{ mt: 2 }} container alignItems="center" spacing={2}>
        <FormControl size="small" sx={{ width: '6em' }}>
          <InputLabel id="payouts-page-size-label">Page size</InputLabel>
          <Select
            labelId="payouts-page-size-label"
            value={pageSize}
            label="Page size"
            onChange={handlePageSizeChange}
          >
            {pageSizes.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Pagination
          count={count}
          page={page}
          siblingCount={1}
          boundaryCount={1}
          variant="outlined"
          shape="rounded"
          onChange={handlePageChange}
          size="large"
        />
      </Grid>
      <Snackbar
        open={Boolean(receiptError)}
        autoHideDuration={5000}
        onClose={() => setReceiptError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setReceiptError(null)}>
          {receiptError}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Payouts;
